import { ChangeDetectionStrategy, Component, input, OnInit, output, viewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { ButtonModel, LookupModel } from '../../../../../api/models';
import { CommonModule } from '@angular/common';
import { ButtonTypes } from '../enums';
import { MatSimpleSelectComponent } from '../../../../../editors/mat-simple-select/mat-simple-select.component';
import { BUTTON_TYPE_DESCRIPTIONS } from '../constants/button-type-descriptions';
import { BUTTON_PARAMETER_FIELD_LABELS } from '../constants/button-parameter-field-labels';
import { MatTextInputComponent } from '../../../../../editors/mat-text-input/mat-text-input.component';
import { PhoneNumberSanitizerDirective } from '../../../../../core/directives/phone-number-sanitizer.directive';
import { DeviceButtonSharedService } from '../services/device-button-shared.service';
import { BUTTON_TYPES } from '../constants/device-button-types';

@Component({
  selector: 'app-device-button',
  standalone: true,
  imports: [
    MatExpansionModule,
    ReactiveFormsModule,
    CommonModule,
    MatSimpleSelectComponent,
    MatTextInputComponent,
    PhoneNumberSanitizerDirective,
  ],
  templateUrl: './device-button.component.html',
  styleUrl: './device-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceButtonComponent implements OnInit {
  public readonly buttonTypes = ButtonTypes;

  public readonly expansionPanel = viewChild(MatExpansionPanel);
  public readonly expansionClosed = output<void>();
  public readonly expansionOpened = output<void>();
  public readonly buttonForm = input.required<UntypedFormGroup>();
  public lookupButtonTypes = [];
  public disabledLookupButtonTypes = [];

  public readonly formSubmitted = input<boolean>(false);
  public readonly btnIndex = input<number>(0);

  public readonly nonSelectableTypes = [{ id: ButtonTypes.Local, text: '' }];
  public isDisabled = false;

  constructor(private readonly _deviceButtonSharedService: DeviceButtonSharedService) {}

  get selectedLookUpTypes() {
    if (this.isDisabled) {
      return this.disabledLookupButtonTypes;
    }
    return this.lookupButtonTypes;
  }

  get buttonData(): ButtonModel {
    return this.buttonForm().getRawValue() || {};
  }

  get parameterValues(): LookupModel[] {
    const { type } = this.buttonData;

    return this._deviceButtonSharedService.lookupButtonParametersMap?.[type] || [];
  }

  get buttonTitle(): string {
    const { sort, name } = this.buttonData;

    if (sort && name) {
      return `${name} ${sort}`;
    }

    return `Template Button ${this.btnIndex() + 1}`;
  }

  get buttonLabel(): string {
    const { label } = this.buttonData;

    return label || this.defaultLabel;
  }

  get buttonTypeDescription(): string {
    const { type } = this.buttonData;

    return this.isDisabled ? 'This button cannot be edited' : BUTTON_TYPE_DESCRIPTIONS[type];
  }

  get isParameterVisible(): boolean {
    const { type } = this.buttonData;

    return this._deviceButtonSharedService.isParameterVisible(type as ButtonTypes);
  }

  get parameterFieldLabel(): string {
    const { type } = this.buttonData;

    return BUTTON_PARAMETER_FIELD_LABELS[type] || 'Parameter';
  }

  get defaultLabel(): string {
    const { type, parameter } = this.buttonData;

    return this._deviceButtonSharedService.getDefaultLabel(type as ButtonTypes, parameter);
  }

  public ngOnInit(): void {
    this.mapLookupButtonTypes();

    this.isDisabled = this.buttonForm().disabled;

    if (this.isDisabled) {
      this.buttonForm().clearValidators();
    }
  }

  public onParameterChange(): void {
    this.buttonForm().get('label')?.setValue(this.defaultLabel);
  }

  public onButtonTypeChange(btnType: ButtonTypes): void {
    this._deviceButtonSharedService.onButtonTypeChange(btnType, this.buttonForm());
  }

  private mapLookupButtonTypes(): void {
    const buttonTypes = BUTTON_TYPES || [];

    const enabled: { id: string; text: string }[] = [];
    const disabled: { id: string; text: string }[] = [];

    for (const btn of buttonTypes) {
      const entry = { id: btn.value, text: btn.title };

      if (btn.disabled) {
        disabled.push(entry);
      } else {
        enabled.push(entry);
      }
    }

    this.lookupButtonTypes = enabled;
    this.disabledLookupButtonTypes = disabled;
  }
}
